/* eslint-disable max-len */
import { observer } from 'mobx-react';
import { EXTENSION_FILE_MAPPER, SHARE_KNOWLEDGE_FILE_TYPE, SHARE_KNOWLEDGE_FILE_TYPE_MAPPER, SHARE_KNOWLEDGE_VIEW_STATUS, SHARE_KNOWLEDGE_VIEW_STATUS_MAPPER } from '../../core/configs/constants';
import { useStore } from '../../core/utils/hook';
import { useCallback, useEffect, useState } from 'react';
import { downloadFileBySignedUrl, splitFileName } from '../../core/utils/common';
import Breadcrumb from '../Profile/components/Breadcrumb';
import Pagination from '../Courses/components/Pagination';
import debounce from 'lodash.debounce';
import { ShareKnowledgeVideoPlayer } from '../../components';
import { flowResult } from 'mobx';

const ModalVideoContent = ({ video, fileId, thumbnail, resumeTime, viewFileProgress, onUpdateViewStatus }) => {
    if (!video) {
        return (
            <div className='not-show-video'>
                <img src={'/images/img-1.png'} alt='' style={{ height: '100%', objectFit: 'fill' }} />
                <p>後にご覧いただけます。</p>
            </div>
        );
    }

    // Save progress, then reflect the server-computed view status in place.
    const syncProgress = async (segments, lastTimeViewed) => {
        const result = await viewFileProgress(fileId, { segments, lastTimeViewed });
        if (result?.viewStatus !== undefined) {
            onUpdateViewStatus(result.viewStatus);
        }
    };

    return (
        <ShareKnowledgeVideoPlayer
            options={{
                poster: thumbnail,
                sources: [
                    {
                        src: video,
                        type: video.toLowerCase().includes('m3u8') ? 'application/x-mpegURL' : 'video/mp4'
                    }
                ]
            }}
            width={570}
            height={250}
            disableForward={false}
            disableAutoPause={true}
            // Resume from the last viewed position (the API returns 0 once the video was watched through).
            lastTimeViewed={resumeTime}
            onHeartbeat={syncProgress}
            onEndVideo={syncProgress}
        />
    );
};

const ViewStatusBadge = ({ viewStatus }) => {
    const config = SHARE_KNOWLEDGE_VIEW_STATUS_MAPPER[viewStatus]
        || SHARE_KNOWLEDGE_VIEW_STATUS_MAPPER[SHARE_KNOWLEDGE_VIEW_STATUS.NOT_VIEWED];
    return (
        <span
            className='view-status-badge'
            style={{ color: config.COLOR, backgroundColor: config.BG_COLOR }}
        >
            <img className='view-status-icon' src={config.ICON} alt='' />
            {config.TEXT}
        </span>
    );
};

const ShareKnowledgeFileListScreen = observer(() => {
    // store
    const {
        shareKnowledgeStore: { lstFileShareknowledge, getAllFileShareknowledge, getFileInfo,
            pagingLstFileShareknowledge, clearLstFileShareknowledge, getPresignedUrlsDonwload, viewFileProgress,
            updateLocalViewStatus },
        modalStore: { show, hide }} = useStore();

    const [fileName, setFileName] = useState('');
    const [type, setType] = useState('');
    const [sortBy, setSortBy] = useState('DESC');
    const [viewStatus, setViewStatus] = useState('');

    const onFetchData = useCallback((data, isHideLoading = false) => {
        getAllFileShareknowledge({ fileName, type, sortBy, viewStatus, ...data }, isHideLoading);
    }, [type, sortBy, viewStatus]);

    useEffect(() => {
        // Reset to the first page whenever a filter/sort changes so results are not stuck on a stale page.
        onFetchData({ page: 1 });
        return () => {
            clearLstFileShareknowledge();
        }
    }, [type, sortBy, viewStatus, onFetchData]);
        
    const onDebounceSearch = useCallback(
        debounce((fileName) => {
            onFetchData({ fileName }, true);
        }, 500),
        [onFetchData]
    );

    const onDownload = async ({ id, name, url }) => {
        const { ext } = splitFileName(name);
        const res = await getPresignedUrlsDonwload({
            id
        });
        if (ext === 'pdf') {
            if (res) {
                window.open(url, '_self');
            }
        } else {
            if (res) {
                downloadFileBySignedUrl(res.url);
            }
        }
    };

    const onShowModalVideo = async (video, fileName, fileId, thumbnail) => {
        const fileInfo = await flowResult(getFileInfo(fileId));

        if (fileInfo) {
            show({
                id: 'modal-play-video-share-knowledge',
                header: <div className='line-clamp-1' style={{ wordBreak: 'break-all' }}>{fileName}</div>,
                isOpen: true,
                onCancel: () => {
                    hide();
                },
                children: (
                    <div style={{ padding: '15px' }}>
                        <ModalVideoContent
                            video={video}
                            fileId={fileId}
                            thumbnail={thumbnail}
                            resumeTime={fileInfo.lastTimeViewed ?? 0}
                            viewFileProgress={viewFileProgress}
                            onUpdateViewStatus={(status) => updateLocalViewStatus(fileId, status)}
                        />
                    </div>
                ),
                type: 'medium max-width-600'
            });
        }
    };
    return (
        <div
            id="main"
            className="wrapper main-account-trgisstration-1 main-account-trgisstration-2 main-account-trgisstration-4 main-update main-send-inquiry"
        >
            <div className='shareknowledge-file-screen container'>
                <Breadcrumb titlePage='シェアナレッジ' />
                <div className='title'>シェアナレッジ</div>
                <div className='filter-container'>
                    <div className='filter'>
                        <div className='filter-filename'>
                            <input
                                onChange={(e) => {
                                    setFileName(e.target.value);
                                    onDebounceSearch(e.target.value);
                                }}
                                className='filter-filename-input'
                                placeholder='ファイル名で検索' />
                            <img className='filter-filename-icon' src='/images/icon-search-share-knowledge.svg' alt='' />
                        </div>
                        <div className='filter-type'>
                            <select onChange={(e) => setType(e.target.value)}>
                                <option value=''>全ファイル</option>
                                {
                                    Object.values(SHARE_KNOWLEDGE_FILE_TYPE).map((value, index) => (
                                        <option key={index} value={value}>{SHARE_KNOWLEDGE_FILE_TYPE_MAPPER[value].TEXT}</option>
                                    ))
                                }
                            </select>
                        </div>
                        <div className='filter-status'>
                            <select value={viewStatus} onChange={(e) => setViewStatus(e.target.value)}>
                                <option value=''>全ステータス</option>
                                {
                                    Object.values(SHARE_KNOWLEDGE_VIEW_STATUS_MAPPER).map((item) => (
                                        <option key={item.VALUE} value={item.VALUE}>{item.TEXT}</option>
                                    ))
                                }
                            </select>
                        </div>
                    </div>
                    <div className='sort'>
                        <div className='sort-title'>
                            <img src='/images/icon-filter.svg' alt='' />
                            <span>ソート</span>
                        </div>
                        <select onChange={(e) => setSortBy(e.target.value)}>
                            <option value='DESC'>新しい順</option>
                            <option value='ASC'>古い順</option>
                        </select>
                    </div>
                </div>
                <div className='content-lst'>
                    {
                        lstFileShareknowledge?.length > 0 ? 
                            lstFileShareknowledge.map((file, index) => {
                                const { ext } = splitFileName(file.name);
                                const typeFile = Object.keys(EXTENSION_FILE_MAPPER).filter(key => EXTENSION_FILE_MAPPER[key].VALUE.includes(ext));
                                let icon = '/images/img-1-1.png';
                                if (typeFile?.length > 0) {
                                    icon = EXTENSION_FILE_MAPPER[typeFile[0]].ICON;
                                }
                                return (
                                    <div key={index} className='share-knowledge-content-item'>
                                        <div className='share-knowledge-content-item-info'>
                                            {
                                                file.type === SHARE_KNOWLEDGE_FILE_TYPE.VIDEO ? (
                                                    <img
                                                        className='thumbnail cursor-pointer'
                                                        src={file?.thumbnail ? file.thumbnail : '/images/img-1-1.png'}
                                                        onClick={() => onShowModalVideo(file.playUrl, file.name, file.id, file?.thumbnailUrl)}
                                                        alt=''></img>
                                                ) : (
                                                    typeFile?.length > 0 && typeFile[0] === 'IMAGE' ? (
                                                        <div
                                                            onClick={() => onDownload(file)}
                                                            className='cursor-pointer flex-none cursor-pointer'>
                                                            <img style={{ width: '45px', height: '45px', borderRadius: '5px' }}
                                                                src={file.url} alt='' />
                                                        </div>
                                                    ) : (
                                                        <div
                                                            onClick={() => onDownload(file)}
                                                            className='thumbnail-doc cursor-pointer'>
                                                            <img className='thumbnail' src={icon} alt='' />
                                                        </div>
                                                    )
                                                )
    
                                                    
                                                
                                            }
                                            <div className='name-status'>
                                                <div
                                                    className='file-name line-clamp-2 cursor-pointer'
                                                    onClick={file.type === SHARE_KNOWLEDGE_FILE_TYPE.VIDEO ?
                                                        () => onShowModalVideo(file.playUrl, file.name, file.id, file?.thumbnailUrl) :
                                                        () => onDownload(file)}
                                                >{file.name}</div>
                                                {file.type === SHARE_KNOWLEDGE_FILE_TYPE.VIDEO && (
                                                    <ViewStatusBadge viewStatus={file.viewStatus} />
                                                )}
                                            </div>
                                        </div>
                                        <div className='share-knowledge-content-item-action'>
                                            {
                                                file.type === SHARE_KNOWLEDGE_FILE_TYPE.VIDEO ? (
                                                    <div className='share-knowledge-action-item'
                                                        onClick={() => onShowModalVideo(file.playUrl, file.name, file.id, file?.thumbnailUrl)}>
                                                        <img src='/images/icon-play-share-knowledge.svg' />
                                                    </div>
                                                ) : <div style={{ width: '30px', height: '30px' }}></div>
                                            }
                                            {file.type !== SHARE_KNOWLEDGE_FILE_TYPE.VIDEO && (
                                                <div
                                                    className='share-knowledge-action-item'
                                                    onClick={() => onDownload(file)}>
                                                    <img src='/images/icon-download-share-knowledge.svg' />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )
                            })
                            : <div
                                className='d-flex align-items-center justify-content-center min-height-200'
                                style={{ gridColumn: 'span 2 / span 2' }}>データはありません</div>
                    }
                </div>
                <Pagination paging={pagingLstFileShareknowledge} onFetch={onFetchData} />
            </div>
        </div>
    )
});

export default ShareKnowledgeFileListScreen;